import { Box, Stack, Grid, Typography, TextField } from '@mui/material';
import React, { useState } from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NavBar from '../components/navbar';
import Footer from '../components/footer';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import ContactInfoRow from '../components/ContactInfoRow';
import MagneticButton from '../components/animated/MagneticButton';
import ScrollReveal from '../components/animated/ScrollReveal';
import { gradients, colors, shadows, radii } from '../theme/designTokens';
import { createAlegionContact, normalizePhone, getContactSubmitErrorMessage } from '../services/contactService';
import FeedbackModal from '../components/FeedbackModal';
import TurnstileField from '../components/TurnstileField';
import HoneypotField from '../components/HoneypotField';

const lightFieldSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: radii.sm,
    background: colors.white,
    '& fieldset': { borderColor: colors.border },
    '&:hover fieldset': { borderColor: colors.navy },
    '&.Mui-focused fieldset': { borderColor: colors.navy, borderWidth: 1 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: colors.navy },
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email_id: '',
    phone: '',
    message: '',
    website: '',
  });
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [modal, setModal] = useState({ open: false, type: 'success', title: '', message: '' });

  const showModal = (type, title, message) => {
    setModal({ open: true, type, title, message });
  };

  const closeModal = () => {
    setModal((prev) => ({ ...prev, open: false }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'phone' ? value.replace(/\D/g, '').slice(0, 10) : value,
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) return 'Name is required.';
    const email = formData.email_id.trim();
    if (!email) return 'Email address is required.';
    if (!/\S+@\S+\.\S+/.test(email)) {
      return 'Please enter a valid email address.';
    }
    if (!formData.phone.trim()) return 'Phone number is required.';
    if (normalizePhone(formData.phone).length !== 10) {
      return 'Please enter a valid 10-digit mobile number.';
    }
    if (!turnstileToken) {
      return 'Please complete the CAPTCHA verification.';
    }
    return null;
  };

  const resetBotFields = () => {
    setTurnstileToken('');
    setTurnstileResetKey((k) => k + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    const errorMsg = validateForm();
    if (errorMsg) {
      setError(errorMsg);
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await createAlegionContact({
        ...formData,
        turnstile_token: turnstileToken,
      });
      if (result.email_sent === false) {
        setError('Your details were saved but the email notification could not be sent. Please call us at +91 95000 82260.');
        showModal(
          'error',
          'Email not delivered',
          'Your details were saved, but we could not send the email notification. Please call us at +91 95000 82260 or try again later.'
        );
        resetBotFields();
        return;
      }
      setFormData({ name: '', email_id: '', phone: '', message: '', website: '' });
      resetBotFields();
      showModal(
        'success',
        'Message sent!',
        "Thank you for reaching out. We've sent a confirmation to your email and our team will get back to you within one business day."
      );
    } catch (err) {
      const message = getContactSubmitErrorMessage(err);
      setError(message);
      showModal('error', 'Submission failed', message);
      resetBotFields();
      console.error('Error submitting form:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack sx={{ bgcolor: colors.white, overflow: 'hidden' }}>
      <NavBar />

      <PageHero
        variant="dark"
        eyebrow="Contact"
        title="Simple. Elegant."
        titleHighlight="Ready when you are."
        subtitle="Begin a conversation about resilience architecture, AERA™, or how ALEgION can support your organization."
        badges={['Executive Briefings', 'AERA™', 'Resilience Advisory']}
        minHeight={{ xs: 300, md: 400 }}
      />

      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 8, md: 10 },
          background: gradients.sectionLight,
        }}
      >
        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
          <Grid container spacing={{ xs: 4, md: 6 }}>
            <Grid item xs={12} md={6}>
              <ScrollReveal>
                <Box sx={{ p: { xs: 0, md: 1 } }}>
                  <SectionHeader
                    eyebrow="Message"
                    title="Start a conversation"
                    highlightWords={['conversation']}
                    subtitle="Our team responds within one business day."
                    align="left"
                  />
                  <Box component="form" noValidate onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      variant="outlined"
                      value={formData.name}
                      name="name"
                      onChange={handleInputChange}
                      sx={lightFieldSx}
                      required
                    />
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      variant="outlined"
                      value={formData.email_id}
                      name="email_id"
                      onChange={handleInputChange}
                      sx={lightFieldSx}
                      required
                    />
                    <TextField
                      fullWidth
                      label="Phone Number"
                      variant="outlined"
                      value={formData.phone}
                      name="phone"
                      onChange={handleInputChange}
                      inputProps={{ maxLength: 10, pattern: '[0-9]*' }}
                      sx={lightFieldSx}
                      required
                    />
                    <TextField
                      fullWidth
                      label="Message"
                      multiline
                      rows={4}
                      variant="outlined"
                      value={formData.message}
                      name="message"
                      onChange={handleInputChange}
                      inputProps={{ maxLength: 2000 }}
                      sx={{ ...lightFieldSx, mb: 2 }}
                    />
                    <HoneypotField
                      value={formData.website}
                      onChange={handleInputChange}
                    />
                    <TurnstileField
                      resetKey={turnstileResetKey}
                      onToken={setTurnstileToken}
                      onExpire={() => setTurnstileToken('')}
                    />
                    <MagneticButton
                      type="submit"
                      variant="navy"
                      fullWidth
                      disabled={isSubmitting || !turnstileToken}
                    >
                      {isSubmitting ? 'Submitting...' : 'Send Message'}
                    </MagneticButton>
                  </Box>
                  {error && (
                    <Typography color="error" sx={{ mt: 2, fontSize: '0.9rem' }}>
                      {error}
                    </Typography>
                  )}
                </Box>
              </ScrollReveal>
            </Grid>

            <Grid item xs={12} md={6}>
              <ScrollReveal delay={0.1}>
                <Box
                  sx={{
                    p: { xs: 3, md: 4 },
                    border: `1px solid ${colors.borderLight}`,
                    background: colors.white,
                    height: '100%',
                  }}
                >
                  <SectionHeader
                    eyebrow="Direct"
                    title="Connect with us"
                    highlightWords={['us']}
                    align="left"
                  />
                  <ContactInfoRow
                    light
                    icon={<LocationOnIcon sx={{ color: colors.navy }} />}
                    label="Address"
                    value="ALEgION, New No. 7C, Perumal Koil Street, Nerkundram, Chennai, Tamil Nadu - 600107"
                  />
                  <ContactInfoRow
                    light
                    icon={<EmailIcon sx={{ color: colors.navy }} />}
                    label="Email"
                    value="info@alegion.net"
                  />
                  <ContactInfoRow
                    light
                    icon={<PhoneIcon sx={{ color: colors.navy }} />}
                    label="Phone"
                    value="+91 95000 82260"
                  />
                  <Box
                    sx={{
                      mt: 3,
                      p: 2.5,
                      border: `1px solid ${colors.borderLight}`,
                      display: 'flex',
                      gap: 2,
                      alignItems: 'flex-start',
                    }}
                  >
                    <AccessTimeIcon sx={{ color: colors.navyMid, mt: 0.25 }} />
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: colors.slate, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                        Office Hours
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: colors.slate, lineHeight: 1.7 }}>
                        Monday – Friday: 9:00 AM – 6:00 PM IST
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </ScrollReveal>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 6, md: 8 },
          background: colors.white,
        }}
      >
        <ScrollReveal>
          <SectionHeader
            eyebrow="Location"
            title="Visit our office"
            highlightWords={['office']}
            subtitle="Nerkundram, Chennai."
          />
        </ScrollReveal>
        <ScrollReveal delay={0.1}>
          <Box
            sx={{
              maxWidth: 1000,
              mx: 'auto',
              overflow: 'hidden',
              boxShadow: shadows.md,
              border: `1px solid ${colors.borderLight}`,
              height: { xs: 280, sm: 380, md: 420 },
            }}
          >
            <iframe
              title="ALEgION Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3916.3039425138967!2d80.1857415!3d13.0699359!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a52612305ecaaef%3A0x352391da1fea98f1!2sALEgION%20Insurance%20Broking%20Limited!5e0!3m2!1sen!2sin!4v1679485192135!5m2!1sen!2sin"
              style={{ border: 0, width: '100%', height: '100%' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Box>
        </ScrollReveal>
      </Box>

      <Footer />

      <FeedbackModal
        open={modal.open}
        onClose={closeModal}
        type={modal.type}
        title={modal.title}
        message={modal.message}
      />
    </Stack>
  );
};

export default Contact;
